import { ID } from '@datorama/akita';
import { Coordinate } from 'ol/coordinate';

import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { Entity, Column } from '../entity/index';
import { IEntity } from '../interfaces/models';
import { IRepRosterSalesRep } from './repRoster.model';
import { WalklistType } from '../lib/common';

export interface IWalklist extends IEntity {
  walklistPin: string;
  walklistName: string;
  walklistType: string;
  walklistActive: boolean;
  // TODO: check if we need to change this to LineString
  walklistGeom: Coordinate[][][];
  walklistDescription: string;
  salesReps: string[];
  salesRepIds: string[]; // CPMS internal salesRepIds array
  territoryId: number;
  territoryName: string;
  territory: string;
  created: string;
  modified: string;
  territoryType: string;
  vendorSalesRepName: string;
  assignedDate?: string | any;
  stops: number;
  walklistKey: string;
  completion: number;
}

@Entity({
  name: PageTitles.walklist,
  language: LanguagePage.walklist,
})
export class Walklist implements IWalklist {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  walklistPin: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  walklistName: string;

  // DPD-1171
  @Column({
    type: ColumnType.number,
    table: {
      defaultPosition: 3,
    },
  })
  stops: number;

  // it's fixed to 'S' walklist type since DRM2.0
  // move end of column
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 12,
      hide: true,
    },
    form: {
      defaultPosition: 1,
    },
  })
  walklistType: string;

  @Column({
    type: ColumnType.boolean,
    isEditable: true,
    table: {
      defaultPosition: 3,
    },
    form: {
      defaultPosition: 2,
    },
  })
  walklistActive: boolean;

  @Column({
    type: ColumnType.string,
    isEditable: true,
    table: {
      defaultPosition: 4,
    },
    form: {
      defaultPosition: 3,
    },
  })
  walklistDescription: string;

  @Column({
    type: ColumnType.option,
    isEditable: true,
    table: {
      defaultPosition: 5,
    },
    form: {
      defaultPosition: 4,
    },
  })
  vendorSalesRepName: string;

  @Column({
    type: ColumnType.option,
    isEditable: true,
    table: {
      defaultPosition: 5,
    },
    form: {
      defaultPosition: 4,
    },
  })
  assignedDate: Date;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
      hide: true,
    },
    form: {
      defaultPosition: 5,
    },
  })
  territoryName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 7,
      hide: true,
    },
  })
  createdBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 8,
      hide: true,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 9,
      hide: true,
    },
  })
  modifiedBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 10,
      hide: true,
    },
  })
  modifiedAt: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 6,
    },
  })
  territoryType: string;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 7,
    },
  })
  created: string;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 8,
    },
  })
  modified: string;

  id: ID;
  walklistGeom: Coordinate[][][];
  salesReps: string[];
  salesRepIds: string[]; // NEW: CPMS salesRepId array
  territoryId: number;
  territory: string;
  walklistKey: string;
  completion: number;

  constructor(
    walklist: IWalklist = {
      id: '',
      walklistPin: '',
      walklistName: '',
      walklistType: '',
      walklistActive: false,
      walklistGeom: [],
      walklistDescription: '',
      territoryId: 0,
      territoryName: '',
      territory: '',
      created: '',
      modified: '',
      territoryType: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      vendorSalesRepName: '',
      salesReps: [],
      salesRepIds: [],
      stops: 0,
      assignedDate: new Date(),
      walklistKey: '',
      completion: 0,
    }
  ) {
    const {
      id,
      walklistPin,
      walklistName,
      walklistType,
      walklistActive,
      walklistGeom,
      walklistDescription,
      salesReps,
      salesRepIds,
      territoryId,
      territoryName,
      territory,
      created,
      modified,
      territoryType,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      vendorSalesRepName,
      stops,
      assignedDate,
      walklistKey,
      completion,
    } = walklist;

    this.id = id;
    this.walklistPin = walklistPin;
    this.walklistName = walklistName;
    this.walklistType = walklistType;
    this.territoryName = territoryName;
    this.walklistActive = walklistActive;
    this.walklistGeom = walklistGeom;
    this.walklistDescription = walklistDescription;
    this.salesReps = salesReps;
    this.salesRepIds = salesRepIds;
    this.territoryId = territoryId;
    this.territory = territory;
    this.created = created;
    this.modified = modified;
    this.territoryType = territoryType;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
    this.assignedDate = assignedDate;
    this.vendorSalesRepName = vendorSalesRepName;
    this.stops = stops;
    this.walklistKey = walklistKey;
    this.completion = completion;
  }

  static createFromDTO(
    record: IWalklist,
    salesReps: IRepRosterSalesRep[]
  ): Walklist {
    const modifiedBy = salesReps.filter(
      (vendor) => vendor.CPMS_ID__c === record.modifiedBy
    )[0]?.Name;
    const createdBy = salesReps.filter(
      (vendor) => vendor.CPMS_ID__c === record.createdBy
    )[0]?.Name;
    const vendorSalesRepName = record.salesReps
      .map((salesRep) => {
        const vendor = salesReps.find((rep) => rep.CPMS_ID__c === salesRep);
        return vendor ? vendor.Name : salesRep;
      })
      .filter((name) => name !== '')
      .join(', ');

    return new Walklist({
      ...record,
      vendorSalesRepName,
      territoryName: record.territory,
      walklistType:
        record.walklistType === 'S' || record.walklistType === 'Sales'
          ? WalklistType.sales
          : WalklistType.dropPermission,
      modifiedBy: modifiedBy ? modifiedBy : record.modifiedBy,
      createdBy: createdBy ? createdBy : record.createdBy,
    });
  }
}
